import type {
  SanityLinkValue,
  SanityMediaValue,
  SanityPageBuilderEditorialCardsArticleCard,
  SanityPageBuilderEditorialCardsCaseStudyCard,
  SanityPageBuilderEditorialCardsInlineCard,
  SanityPageBuilderEditorialCardsReferencedDocument,
  SanityPageBuilderEditorialCardsSection,
  SanityPageBuilderEditorialCardsWebinarCard,
  SanityPageBuilderSection,
  SanityRichTextValue,
} from "@ouster/shared";

import { nonNullable, takeFirst } from "~/features/app/utils";
import { Tag } from "~/features/ui/tag";
import { ArticleCardView } from "~/features/card/article-card";
import { CaseStudyCardView } from "~/features/card/case-study-card";
import { EditorialCard } from "~/features/card/editorial-card";
import { EditorialFeaturedCard } from "~/features/card/editorial-featured-card";
import { ShowcaseCard } from "~/features/card/showcase-card";
import { WebinarCardView } from "~/features/card/webinar-card";
import { EditorialCardsBlockView, type EditorialCardsCardLayout } from "~/features/blocks/inventory/editorial-cards";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
import { hasRenderableSanityMedia, SanityMediaFigure } from "~/features/sanity/page-builder/sections/media-figure";
import { toCraftLinkArray } from "~/features/sanity/link";
import { toHtml } from "~/features/sanity/rich-text/to-html";

type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderEditorialCardsSection;
  };
};

type EditorialCardsViewCard = {
  _key?: string;
  type?: string;
  render: (layout: EditorialCardsCardLayout) => React.ReactNode;
  isShowcase: boolean;
};

// Filter labels are keyed by the Sanity card `_type` so the type filter has
// stable option labels.
const TYPE_LABELS: Record<string, string> = {
  editorialCardsArticleCard: "Articles",
  editorialCardsCaseStudyCard: "Case Studies",
  editorialCardsWebinarCard: "Webinars",
  editorialCardsGeneralCard: "General",
  editorialCardsShowcaseCard: "Showcase",
};

function getPlainLinkIcon(link?: { newWindow?: boolean } | null) {
  if (!link) {
    return undefined;
  }

  return link.newWindow ? ArrowTopRight : ArrowRight;
}

function buildSectionLink(link?: SanityLinkValue) {
  const cta = takeFirst(toCraftLinkArray(link));

  if (!isLinkValid(cta)) {
    return undefined;
  }

  return <Button as={Link} {...cta} icon={getPlainLinkIcon(cta)} variant="underline" />;
}

function refHero(ref?: SanityPageBuilderEditorialCardsReferencedDocument) {
  return ref?.hero?.[0];
}

export function refHeadline(ref?: SanityPageBuilderEditorialCardsReferencedDocument) {
  return ref?.headline || ref?.title || undefined;
}

export function refHref(ref?: SanityPageBuilderEditorialCardsReferencedDocument) {
  return ref?.href || ref?.url || ref?.uri || undefined;
}

export function refText(ref?: SanityPageBuilderEditorialCardsReferencedDocument) {
  const hero = refHero(ref);
  return hero?.text ? toHtml(hero.text) : undefined;
}

// Uses the `ArticleTags`/`CaseStudyTags` markup (`<ul>` of blurred
// `Tag`s) so reference cards render their category/industry tags. Article shows
// `[category, industry]`; caseStudy shows its `industries[]`.
function refTags(ref?: SanityPageBuilderEditorialCardsReferencedDocument) {
  const industry = ref?.industry;
  const industries = Array.isArray(industry) ? industry : industry ? [industry] : [];
  const items = [ref?.category, ...industries].filter(nonNullable);

  if (!items.length) {
    return undefined;
  }

  return (
    <ul tw="flex gap-4 z-10 relative">
      {items.map((item, index) => (
        <Tag as="li" key={item._id ?? index} variant="blur">
          {item.title}
        </Tag>
      ))}
    </ul>
  );
}

export function refMedia(ref?: SanityPageBuilderEditorialCardsReferencedDocument, ratio?: Ratio) {
  const media = refHero(ref)?.media;

  if (!hasRenderableSanityMedia(media)) {
    return undefined;
  }

  return <SanityMediaFigure media={media} ratio={ratio} fit="fill" />;
}

function inlineCardMedia(media?: SanityMediaValue, ratio?: Ratio) {
  if (!hasRenderableSanityMedia(media)) {
    return undefined;
  }

  return <SanityMediaFigure media={media} ratio={ratio} />;
}

function inlineCardText(text?: SanityRichTextValue) {
  return text?.length ? toHtml(text) : undefined;
}

export function SanityEditorialCardsSection({ section }: Props) {
  const { sectionContent, sectionSettings } = section;

  const cards =
    sectionContent.cards?.map<EditorialCardsViewCard>((card) => {
      const type = card._type;

      if (type === "editorialCardsArticleCard") {
        const ref = (card as SanityPageBuilderEditorialCardsArticleCard).article;

        return {
          _key: card._key,
          type,
          isShowcase: false,
          render: ({ featured, cardSize, ratio }) => (
            <ArticleCardView
              featured={featured}
              size={cardSize}
              headline={refHeadline(ref)}
              text={refText(ref)}
              url={refHref(ref)}
              media={refMedia(ref, ratio)}
              tags={refTags(ref)}
            />
          ),
        };
      }

      if (type === "editorialCardsCaseStudyCard") {
        const ref = (card as SanityPageBuilderEditorialCardsCaseStudyCard).caseStudy;

        return {
          _key: card._key,
          type,
          isShowcase: false,
          render: ({ featured, cardSize }) => (
            <CaseStudyCardView
              featured={featured}
              size={cardSize}
              linkText={cardSize === "large" ? "Read More" : undefined}
              headline={refHeadline(ref)}
              text={refText(ref)}
              url={refHref(ref)}
              media={refMedia(ref, "496/280")}
              tags={refTags(ref)}
            />
          ),
        };
      }

      if (type === "editorialCardsWebinarCard") {
        const ref = (card as SanityPageBuilderEditorialCardsWebinarCard).webinar;

        return {
          _key: card._key,
          type,
          isShowcase: false,
          render: ({ featured, cardSize }) => (
            <WebinarCardView
              featured={featured}
              size={cardSize}
              headline={refHeadline(ref)}
              text={refText(ref)}
              url={refHref(ref)}
              linkText={ref?.linkText}
              media={refMedia(ref, "496/280")}
            />
          ),
        };
      }

      const inline = card as SanityPageBuilderEditorialCardsInlineCard;
      const cta = takeFirst(toCraftLinkArray(inline.link));
      const url = isLinkValid(cta) ? cta.href : undefined;
      const linkText = isLinkValid(cta) ? cta.text : undefined;
      const newWindow = isLinkValid(cta) ? cta.newWindow : undefined;

      if (type === "editorialCardsShowcaseCard") {
        return {
          _key: card._key,
          type,
          isShowcase: true,
          render: ({ featured, cardSize, count }) => {
            const Card = featured ? EditorialFeaturedCard : ShowcaseCard;

            return (
              <Card
                headline={inline.title}
                count={inline.hasCounter ? count : undefined}
                text={inlineCardText(inline.text)}
                size={cardSize}
                hasPlusIcon={false}
                url={url}
                newWindow={newWindow}
                linkText={linkText}
                media={
                  <>
                    <div tw="lg:hidden">{inlineCardMedia(inline.media, "345/275" as Ratio)}</div>
                    <div tw="hidden lg:block">{inlineCardMedia(inline.media, "370/400" as Ratio)}</div>
                  </>
                }
              />
            );
          },
        };
      }

      // editorialCardsGeneralCard (and any other inline card)
      return {
        _key: card._key,
        type,
        isShowcase: false,
        render: ({ featured, cardSize, ratio, count }) => {
          const Card = featured ? EditorialFeaturedCard : EditorialCard;

          return (
            <Card
              headline={inline.title}
              count={inline.hasCounter ? count : undefined}
              text={inlineCardText(inline.text)}
              size={cardSize}
              hasPlusIcon={false}
              url={url}
              linkText={linkText}
              media={inlineCardMedia(inline.media, ratio)}
            />
          );
        },
      };
    }) ?? [];

  return (
    <EditorialCardsBlockView
      anchor={{
        id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
        name: sectionSettings?.anchorName,
      }}
      cards={cards}
      eyebrow={sectionContent.eyebrow}
      headline={sectionContent.headline}
      text={sectionContent.text?.length ? toHtml(sectionContent.text) : undefined}
      link={buildSectionLink(sectionContent.link)}
      hasFilter={sectionContent.hasFilter}
      hasTopDivider={sectionSettings?.hasTopDivider ?? true}
      isHighlighted={sectionContent.isHighlighted}
      mode={sectionSettings?.mode || "light"}
      theme={sectionSettings?.theme || "white"}
      getCardKey={(card, index) => card._key || index}
      getCardType={(card) => card.type}
      isShowcaseCard={(card) => card.isShowcase}
      typeLabels={TYPE_LABELS}
      renderCard={(card, layout) => card.render(layout)}
      sectionProps={{
        "data-sanity-section-key": section._key,
        "data-sanity-section-type": section._type,
      }}
    />
  );
}
